import {
  ReceiptText, ShoppingCart, Truck, Boxes, Calculator, Store,
  UtensilsCrossed, Users, BarChart3, Sparkles, ShieldCheck, Building2,
} from 'lucide-react';

interface Module {
  icon: React.ReactNode;
  title: string;
  desc: string;
  bullets: string[];
}

const modules: Module[] = [
  {
    icon: <ReceiptText className="h-5 w-5" />,
    title: 'Sales & Billing',
    desc:
      'GST-compliant invoicing with sales orders, delivery challans, credit/debit notes and gift cards.',
    bullets: ['e-Invoice (IRN/QR)', 'e-Way Bill', 'Recurring invoices', 'Coupons & promotions'],
  },
  {
    icon: <Truck className="h-5 w-5" />,
    title: 'Purchase',
    desc:
      'End-to-end procure-to-pay: RFQ → PO → GRN → invoice → payment, with supplier ledgers and returns.',
    bullets: ['Purchase orders & GRN', 'Supplier contracts', 'Landed cost', 'Reorder rules'],
  },
  {
    icon: <Boxes className="h-5 w-5" />,
    title: 'Inventory',
    desc:
      'Real-time stock across warehouses with barcoding, batch & bin tracking, and physical-count workflows.',
    bullets: ['Multi-warehouse', 'Stock transfers & adjustments', 'Reorder alerts', 'Stock take'],
  },
  {
    icon: <Store className="h-5 w-5" />,
    title: 'POS',
    desc:
      'Fast, offline-capable point-of-sale with cash-drawer sessions, split bills and multiple payment modes.',
    bullets: ['Touch / barcode billing', 'Cash drawer reconciliation', 'POS shifts', 'Receipts & refunds'],
  },
  {
    icon: <UtensilsCrossed className="h-5 w-5" />,
    title: 'Restaurant',
    desc:
      'Table reservations, KOTs, menu modifiers, recipes, home delivery and tip handling — all in one screen.',
    bullets: ['Table layout & reservations', 'KOT to kitchen', 'Recipe & ingredient costing', 'Home delivery'],
  },
  {
    icon: <Calculator className="h-5 w-5" />,
    title: 'Accounting',
    desc:
      'Double-entry books, journals, ledgers, bank reconciliation and Indian-tax compliance — on the same data.',
    bullets: ['Chart of accounts', 'Bank reconciliation', 'TDS, GST & HSN summary', 'Fiscal year close'],
  },
  {
    icon: <Users className="h-5 w-5" />,
    title: 'CRM',
    desc:
      'Leads, opportunities, quotations and support tickets with loyalty programs and customer segmentation.',
    bullets: ['Sales pipeline', 'Quotations & campaigns', 'Loyalty & segments', 'Support tickets'],
  },
  {
    icon: <BarChart3 className="h-5 w-5" />,
    title: 'HRM & Payroll',
    desc:
      'Employees, attendance, payroll, payslips and statutory deductions — wired to the same accounting books.',
    bullets: ['Attendance & biometric', 'Payroll runs', 'Statutory (PF/ESI/PT/TDS)', 'Leave & shifts'],
  },
  {
    icon: <Sparkles className="h-5 w-5" />,
    title: 'AI & Automation',
    desc:
      'Anomaly detection, recommendations and chat-style insights powered by ML over your live data.',
    bullets: ['Anomaly detection', 'Smart recommendations', 'Workflow approvals', 'Natural-language queries'],
  },
  {
    icon: <ShieldCheck className="h-5 w-5" />,
    title: 'Audit & Compliance',
    desc:
      'Tamper-evident audit trail, GDPR-style consent records, role-based permissions and data retention controls.',
    bullets: ['Per-row audit log', 'Compliance frameworks', 'Data subject requests', 'Security audit log'],
  },
  {
    icon: <ShoppingCart className="h-5 w-5" />,
    title: 'Master Data',
    desc:
      'A single source of truth for products, brands, units, currencies, taxes and customers across all modules.',
    bullets: ['Products & bundles', 'Brands, categories & HSN', 'Multi-currency', 'Price lists & discount schemes'],
  },
  {
    icon: <Building2 className="h-5 w-5" />,
    title: 'Multi-tenant Platform',
    desc:
      'Built tenant-first with branches, subscription plans, feature flags and per-tenant usage metering.',
    bullets: ['Tenant + branch model', 'Subscription billing', 'Feature flags', 'Usage metering'],
  },
];

export default function ModuleShowcase() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
      <div className="max-w-2xl">
        <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          One platform, every module your business needs
        </h2>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)] sm:text-base">
          Twelve tightly-integrated modules — each one production-grade, all built on the same
          tenant-aware data model so figures match across sales, accounting and reporting.
        </p>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {modules.map((m) => (
          <div
            key={m.title}
            className="flex flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                {m.icon}
              </span>
              <h3 className="text-base font-semibold">{m.title}</h3>
            </div>
            <p className="mt-3 text-sm text-[var(--color-text-secondary)]">{m.desc}</p>
            <ul className="mt-3 space-y-1.5 text-xs text-[var(--color-text-muted)]">
              {m.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[var(--color-primary)]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
